import { isObjectV2 } from '@mimir/primitive/type-guards/isObject';

/**
 * Error thrown by MimirClient.send() when the request fails due to a
 * network-level problem (DNS failure, connection refused, timeout, etc.) rather
 * than an HTTP error response from the server.
 *
 * Use `error instanceof NetworkError` to identify network failures. The `code`
 * property carries the errno code when available (e.g. 'ECONNREFUSED',
 * 'ENOTFOUND', 'ETIMEDOUT').
 *
 * Even though there may be multiple implementations of MimirClient, all should
 * throw the same NetworkError class for network failures, so that callers can
 * reliably detect them with instanceof.
 *
 * ```
 *   try {
 *     const response = await client.send(command, {});
 *   } catch (error) {
 *     if (error instanceof NetworkError) {
 *       // server was unreachable
 *     }
 *     if (isHttpError(error) && error.statusCode === 404) {
 *       // server returned 404
 *     }
 *     throw error;
 *   }
 * ```
 */
export class NetworkError extends Error {
  override readonly name = 'NetworkError';

  constructor(
    message: string,
    public readonly code: string | undefined,
    options: { cause: unknown },
  ) {
    super(message, options);
  }
}

const NETWORK_ERROR_CODES = new Set([
  'ECONNREFUSED',  // Connection refused
  'ENOTFOUND',     // DNS lookup failed
  'ETIMEDOUT',     // TCP connect timeout
  'ECONNABORTED',  // Request timeout (Axios)
  'EPROTO',        // Protocol error
  'EAI_AGAIN',     // DNS transient failure
  'ECONNRESET',    // Connection reset
  'EPIPE',         // Broken pipe
]);

/**
 * Detect whether an error is a network-level failure that should be
 * wrapped as a NetworkError. Recognizes:
 * - Errors with known errno codes (ECONNREFUSED, ENOTFOUND, etc.)
 * - Axios network errors (isAxiosError: true with no response)
 */
export function isNetworkLikeError(error: unknown): boolean {
  if (!isObjectV2(error)) {
    return false;
  }
  // Known errno code
  if (typeof error.code === 'string' && NETWORK_ERROR_CODES.has(error.code)) {
    return true;
  }
  // Axios network error (has isAxiosError but no response)
  if (error.isAxiosError === true && error.response == null) {
    return true;
  }
  return false;
}

/**
 * Extract the network error code from an error, checking both the error
 * itself and its cause (Node.js fetch wraps the original error as cause).
 */
export function extractNetworkErrorCode(error: unknown): string | undefined {
  if (!isObjectV2(error)) {
    return undefined;
  }
  if (typeof error.code === 'string' && NETWORK_ERROR_CODES.has(error.code)) {
    return error.code;
  }
  // Node.js fetch wraps the errno error as cause
  if (isObjectV2(error.cause) && typeof error.cause.code === 'string' && NETWORK_ERROR_CODES.has(error.cause.code)) {
    return error.cause.code;
  }
  return undefined;
}
